import { Component, inject, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SearchResourcesDialogComponent } from '../search-resources-dialog/search-resources-dialog.component';
import { NextCalendar } from '../../modules/admin/projects/projects.types';
import { PlanningArea } from '../../modules/admin/services/services.types';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { LocalizeService } from '../../core/pipes/localize.service';

@Component({
    selector: 'app-edit-planning-area-dialog',
    templateUrl: './edit-planning-area-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
        .edit-planning-area-dialog-panel {

            .mat-mdc-dialog-container {
        
                .mat-mdc-dialog-surface {
                    padding: 0 !important;
                }
            }
        }
        `
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSelectModule, ReactiveFormsModule, TranslocoModule]
})
export class EditPlanningAreaComponent implements OnInit {

    areaName: string;
    formGroup = new FormGroup({
        prefix: new FormControl('', [Validators.required, Validators.maxLength(6)]),
        numberOfZombies: new FormControl(0, Validators.min(0)),
        calendarOid: new FormControl('')
    });
    
    calendarGroups = new Map<string, NextCalendar[]>();

    public data: { area: PlanningArea; resources: boolean, calendarList: NextCalendar[] } = inject(MAT_DIALOG_DATA);
    private dialogRef = inject(MatDialogRef<SearchResourcesDialogComponent>);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private localize = inject(LocalizeService);
    private transloco = inject(TranslocoService);

    async ngOnInit() {
        this.areaName = this.localize.transform(this.data.area?.name);
        if (this.data.resources) {
            this.formGroup.get('numberOfZombies').addValidators(Validators.required);
        }
        this.formGroup.patchValue(this.data.area);
        
        for (const calendar of this.data.calendarList) {
            if (!this.calendarGroups.has(calendar.belongsToCaption)) {
                this.calendarGroups.set(calendar.belongsToCaption, []);
            }
            this.calendarGroups.get(calendar.belongsToCaption).push(calendar);
        }
    }

    async save() {
        const confirmSave = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('services.dialog.editPlanningArea.confirm'));
        if (confirmSave) {
            this.data.area = { ...this.data.area, ...this.formGroup.getRawValue() };
            this.dialogRef.close(this.data.area);
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('unsavedChanges'),
            message: message ? message : this.transloco.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }
}
